import { ChangeDetectionStrategy, Component, effect, input, output, signal } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { AgentNotificationSettingsDto, AgentNotificationsUpdateDto } from '@workspace/data-types';

export interface AgentNotificationMandateOption {
  key: string;
  label: string;
}

export const AGENT_NOTIFICATION_MANDATE_OPTIONS: AgentNotificationMandateOption[] = [
  { key: 'exclusif', label: 'Mandat Exclusif' },
  { key: 'co-exclusif', label: 'Co-exclusif / Semi-exclusif' },
  { key: 'simple', label: 'Mandat Simple' },
];

@Component({
  selector: 'etxe-agent-notifications-form',
  imports: [MatIconModule, MatSlideToggleModule, MatFormFieldModule, MatInputModule],
  templateUrl: './agent-notifications-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentNotificationsFormComponent {
  readonly settings = input<AgentNotificationSettingsDto | undefined>(undefined);

  readonly settingsChange = output<AgentNotificationSettingsDto>();
  readonly validityChanged = output<boolean>();
  readonly dtoChanged = output<{ dto: AgentNotificationsUpdateDto; changed: boolean }>();

  readonly mandateOptions = AGENT_NOTIFICATION_MANDATE_OPTIONS;

  readonly internalSettings = signal<AgentNotificationSettingsDto>({
    emailAlertsEnabled: true,
    minPrice: undefined,
    maxPrice: undefined,
    mandateTypes: ['exclusif', 'co-exclusif', 'simple'],
    creditLowAlertEnabled: true,
    creditThreshold: 2,
  });

  constructor() {
    effect(() => {
      const s = this.settings() || {};
      const merged: AgentNotificationSettingsDto = {
        emailAlertsEnabled: s.emailAlertsEnabled ?? true,
        minPrice: s.minPrice,
        maxPrice: s.maxPrice,
        mandateTypes: s.mandateTypes?.length ? [...s.mandateTypes] : ['exclusif', 'co-exclusif', 'simple'],
        creditLowAlertEnabled: s.creditLowAlertEnabled ?? true,
        creditThreshold: s.creditThreshold ?? 2,
      };
      this.internalSettings.set(merged);
      this.validityChanged.emit(true);
      this.dtoChanged.emit({ dto: { notifications: merged }, changed: false });
    });
  }

  toggleEmailAlerts(checked: boolean): void {
    this.updateState({ emailAlertsEnabled: checked });
  }

  toggleCreditAlert(checked: boolean): void {
    this.updateState({ creditLowAlertEnabled: checked });
  }

  onCreditThresholdChange(val: string): void {
    const num = Number.parseInt(val, 10);
    this.updateState({ creditThreshold: Number.isNaN(num) ? 2 : Math.max(1, num) });
  }

  onPriceChange(field: 'minPrice' | 'maxPrice', val: string): void {
    const num = Number.parseInt(val, 10);
    this.updateState({ [field]: Number.isNaN(num) || num <= 0 ? undefined : num });
  }

  toggleMandateType(key: string): void {
    const current = this.internalSettings().mandateTypes || [];
    let updated: string[];
    if (current.includes(key)) {
      // Don't allow empty, keep at least one
      if (current.length <= 1) return;
      updated = current.filter((m) => m !== key);
    } else {
      updated = [...current, key];
    }
    this.updateState({ mandateTypes: updated });
  }

  isMandateSelected(key: string): boolean {
    return (this.internalSettings().mandateTypes || []).includes(key);
  }

  private updateState(partial: Partial<AgentNotificationSettingsDto>): void {
    const updated = {
      ...this.internalSettings(),
      ...partial,
    };
    this.internalSettings.set(updated);
    this.settingsChange.emit(updated);
    this.validityChanged.emit(true);
    this.dtoChanged.emit({
      dto: { notifications: updated },
      changed: true,
    });
  }
}
